import { BottomPlaneIcon, TopPlaneIcon } from '@editor/assets/icons'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

/**
 * Possible icons to be used with Attention Text
 */
export const AttentionTextIcon = {
  bottomPlane: BottomPlaneIcon,
  topPlane: TopPlaneIcon,
}

export interface AttentionTextState {
  /**
   * The message to be displayed
   */
  message: string

  /**
   * The icon to be displayed
   */
  icon?: keyof typeof AttentionTextIcon

  /**
   * The message for the link
   */
  linkMessage?: string

  /**
   * The URL for the link
   */
  linkUrl?: string

  /**
   * The tutorial video configuration
   */
  tutorialVideo?: {
    toolType: string
    variant?: string
    title?: string
    linkMessage: string
  }

  /**
   * Whether the user has manually closed the panel
   */
  isClosedByUser: boolean
}

const initialState: AttentionTextState = {
  message: '',
  isClosedByUser: false,
}

export const slice = createSlice({
  name: 'attentionText',
  initialState,
  reducers: {
    /**
     * Set the message to be displayed
     *
     * @param state Current state
     * @param action Payload action containing the message to be displayed
     */
    setAttentionText: (
      state,
      action: PayloadAction<{
        message: string
        icon?: keyof typeof AttentionTextIcon
        linkMessage?: string
        linkUrl?: string
        tutorialVideo?: {
          toolType: string
          variant?: string
          title?: string
          linkMessage: string
        }
      }>,
    ) => {
      state.message = action.payload.message
      state.icon = action.payload.icon
      state.linkMessage = action.payload.linkMessage
      state.linkUrl = action.payload.linkUrl
      state.tutorialVideo = action.payload.tutorialVideo
      // Reset close state when new message is set
      state.isClosedByUser = false
    },
    /**
     * Close the attention text by user action
     */
    closeAttentionText: (state) => {
      state.message = ''
      state.isClosedByUser = true
    },
    /**
     * Reset the attention text state.
     *
     * @returns The initial state
     */
    reset: () => initialState,
  },
})

export const { setAttentionText, closeAttentionText, reset } = slice.actions

export default slice.reducer
